/**
 * Wallet Preview — pixel-accurate mockups of how the Taco Loco loyalty
 * card will appear on iOS (Apple Wallet) and Android (Google Wallet).
 *
 * Used in iter 175 to validate visual design BEFORE procuring Apple
 * Developer + Google Cloud credentials. Once approved, the same colour /
 * layout choices flow straight into the real pass.json (Apple) and
 * LoyaltyClass (Google) builders.
 */
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import { QRCodeSVG } from 'qrcode.react';
import {
  Smartphone, Apple, Sparkles, RefreshCw, ChevronRight,
  TrendingUp, Award, Check,
} from 'lucide-react';

const STATES = [
  { id: 'low',     label: 'Appena registrato', icon: Sparkles,    desc: 'Bronze, 0 punti' },
  { id: 'default', label: 'Cliente attivo',    icon: TrendingUp,  desc: 'Plata, 320 punti' },
  { id: 'tier_up', label: 'Tier-up Oro',       icon: Award,       desc: 'Appena salito a Oro' },
];

const TIER_COLORS = {
  Bronze:  '#A97142',
  Plata:   '#9CA3AF',
  Oro:     '#D4A017',
  Leyenda: '#8B5CF6',
};

export default function AdminWalletPreview() {
  const [data, setData] = useState(null);
  const [state, setState] = useState('default');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminApi.get(`/admin/wallet/preview?state=${state}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [state]);

  if (loading && !data) {
    return (
      <div className="p-6 md:p-8 lg:p-10">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-wallet-preview">
      <PageTooltip page="wallet-preview" />
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FDECE7] text-[#D44E20] text-xs font-semibold mb-3">
          <Sparkles size={12} />
          Anteprima — pre-credenziali
        </div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Wallet Pass Preview</h1>
        <p className="text-[#64748B] text-sm mt-1 max-w-2xl">
          Ecco esattamente come apparirà la carta fedeltà <strong>{data.brand.name}</strong> nel
          wallet del cliente, su iPhone (Apple Wallet) e Android (Google Wallet).
          I colori, il logo e il layout sono già quelli definitivi —
          appena confermi, procediamo con la richiesta certificati e l'implementazione reale.
        </p>
      </div>

      {/* State picker */}
      <div className="flex flex-wrap gap-2 mb-8" data-testid="wallet-state-picker">
        {STATES.map((s) => {
          const Icon = s.icon;
          const active = state === s.id;
          return (
            <button
              key={s.id}
              onClick={() => setState(s.id)}
              data-testid={`state-${s.id}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-sm border transition-all ${
                active
                  ? 'bg-[#1A1A1A] text-white border-[#1A1A1A] shadow-sm'
                  : 'bg-white text-[#64748B] border-slate-200 hover:border-slate-300'
              }`}
            >
              <Icon size={14} />
              <span className="font-medium">{s.label}</span>
              <span className={`text-xs ${active ? 'text-slate-300' : 'text-[#94A3B8]'}`}>· {s.desc}</span>
            </button>
          );
        })}
      </div>

      {/* The two phones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        <ApplePhonePreview data={data} />
        <AndroidPhonePreview data={data} />
      </div>

      {/* Info section */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <InfoCard
          icon={RefreshCw}
          title="Updates dinamici"
          body="Quando il cliente guadagna o spende punti, il pass si aggiorna automaticamente sul telefono entro 30 secondi. Senza notifiche intrusive."
        />
        <InfoCard
          icon={Check}
          title="QR sempre pronto"
          body="In cassa lo staff scansiona il QR direttamente dallo schermo bloccato dell'iPhone (Lock Screen) o dal Wallet di Android. Zero passaggi extra."
        />
        <InfoCard
          icon={Award}
          title="Tier-up celebration"
          body="Quando il cliente sale di tier (Bronze → Plata → Oro → Leyenda) il pass cambia colore e mostra una notifica di congratulazioni."
        />
      </div>

      {/* What's next */}
      <div className="mt-8 bg-[#111114] rounded-2xl p-6 md:p-8 text-white">
        <h3 className="text-lg font-semibold mb-3">Prossimi step</h3>
        <ol className="space-y-2.5 text-sm">
          <li className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-[#D44E20] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-px">1</span>
            <div>
              <strong className="text-white">Apple Developer Program</strong> — iscrizione (€99/anno),
              creazione Pass Type ID <code className="bg-white/10 px-1.5 py-0.5 rounded text-xs">pass.food.tacoloco.loyalty</code>,
              generazione certificate .p12. <span className="text-slate-400">~30 min + 24h attesa attivazione</span>
            </div>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-[#D44E20] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-px">2</span>
            <div>
              <strong className="text-white">Google Cloud project</strong> — abilita Wallet API,
              crea Service Account, registrati su Google Wallet Business Console.
              <span className="text-slate-400">~20 min</span>
            </div>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-[#D44E20] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-px">3</span>
            <div>
              <strong className="text-white">Backend implementation</strong> — endpoint .pkpass + JWT
              Google + APNs per push updates + integrazione su <code className="bg-white/10 px-1.5 py-0.5 rounded text-xs">/my/&#123;token&#125;</code>.
              <span className="text-slate-400">~5-7 giorni dev + test reale</span>
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// APPLE WALLET MOCKUP — iPhone Pro frame with the Wallet "Store Card" template.
// Visual reference: https://developer.apple.com/wallet/
// ──────────────────────────────────────────────────────────────────────────────

function ApplePhonePreview({ data }) {
  const { brand, customer, pass_metadata } = data;
  const tierColor = TIER_COLORS[customer.tier] || TIER_COLORS.Bronze;
  return (
    <div className="flex flex-col items-center" data-testid="apple-wallet-preview">
      <div className="flex items-center gap-2 mb-4">
        <Apple size={18} className="text-[#1A1A1A]" />
        <span className="font-semibold text-[#1A1A1A]">Apple Wallet</span>
        <span className="text-xs text-[#94A3B8]">· iPhone · Store Card</span>
      </div>
      <div
        className="relative w-[320px] rounded-[44px] p-[14px] shadow-[0_20px_60px_rgba(0,0,0,0.18)]"
        style={{ background: '#0b0b0d' }}
      >
        {/* Notch */}
        <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[100px] h-[28px] bg-black rounded-full z-10" />
        <div className="bg-slate-100 rounded-[34px] pt-10 pb-8 px-3 min-h-[560px] flex flex-col items-center">
          {/* Pass card */}
          <div
            className="w-full rounded-2xl shadow-lg overflow-hidden"
            style={{ background: brand.background_color, color: brand.text_color }}
            data-testid="apple-pass-card"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <div className="flex items-center gap-2 min-w-0">
                {brand.logo_url ? (
                  <img src={brand.logo_url} alt="" className="w-7 h-7 rounded-md object-cover" />
                ) : (
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center text-[10px] font-black"
                    style={{ background: brand.primary_color, color: brand.text_color }}
                  >
                    {brand.name.charAt(0)}
                  </div>
                )}
                <span className="text-[11px] font-semibold uppercase tracking-wider truncate" style={{ color: brand.primary_color }}>
                  {brand.name}
                </span>
              </div>
              <span className="text-[10px] opacity-60">CARTA FEDELTÀ</span>
            </div>

            {/* Strip / hero — flat colour band with the headline figure */}
            <div className="px-4 py-3" style={{ background: `linear-gradient(135deg, ${brand.primary_color}, ${brand.background_color})` }}>
              <div className="text-[10px] opacity-80 uppercase tracking-wider">Punti</div>
              <div className="text-4xl font-black tabular-nums leading-none" style={{ color: brand.text_color }}>
                {customer.points.toLocaleString('it-IT')}
              </div>
            </div>

            {/* Fields grid */}
            <div className="grid grid-cols-3 px-4 py-3 gap-3 border-t border-white/10">
              <PassField label="Tier" value={customer.tier} valueColor={tierColor} />
              <PassField label="Nome" value={customer.name.split(' ')[0]} />
              <PassField label="Visite" value={String(customer.total_visits)} />
            </div>

            {/* Strip notice */}
            {customer.recent_event && (
              <div className="px-4 pb-2 text-[10px] opacity-80 italic border-t border-white/5 pt-2">
                {customer.recent_event}
              </div>
            )}

            {/* QR */}
            <div className="bg-white px-4 py-4 flex flex-col items-center">
              <QRCodeSVG value={pass_metadata.barcode_message} size={120} bgColor="#FFFFFF" fgColor="#000000" level="M" includeMargin={false} />
              <p className="mt-2 text-[10px] text-slate-500 font-mono">{pass_metadata.barcode_message.slice(0, 14)}…</p>
            </div>
          </div>

          {/* Wallet UI footer hint */}
          <p className="text-[10px] text-slate-500 mt-4 text-center max-w-[200px]">
            Scorri il dito dal blocco schermo → il pass appare automaticamente quando sei vicino al locale.
          </p>
        </div>
      </div>
    </div>
  );
}

function PassField({ label, value, valueColor }) {
  return (
    <div>
      <div className="text-[9px] opacity-60 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-semibold tabular-nums" style={{ color: valueColor || 'inherit' }}>{value}</div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// GOOGLE WALLET MOCKUP — Android frame with the Loyalty Card template.
// Visual reference: https://developers.google.com/wallet/retail/loyalty-cards
// ──────────────────────────────────────────────────────────────────────────────

function AndroidPhonePreview({ data }) {
  const { brand, customer, pass_metadata } = data;
  const tierColor = TIER_COLORS[customer.tier] || TIER_COLORS.Bronze;
  return (
    <div className="flex flex-col items-center" data-testid="google-wallet-preview">
      <div className="flex items-center gap-2 mb-4">
        <Smartphone size={18} className="text-[#1A1A1A]" />
        <span className="font-semibold text-[#1A1A1A]">Google Wallet</span>
        <span className="text-xs text-[#94A3B8]">· Android · Loyalty Card</span>
      </div>
      <div
        className="relative w-[320px] rounded-[44px] p-[14px] shadow-[0_20px_60px_rgba(0,0,0,0.18)]"
        style={{ background: '#0b0b0d' }}
      >
        {/* Speaker */}
        <div className="absolute top-[14px] left-1/2 -translate-x-1/2 w-[60px] h-[5px] bg-slate-700 rounded-full z-10" />
        <div className="bg-white rounded-[34px] pt-8 pb-8 min-h-[560px] flex flex-col">
          {/* Status bar */}
          <div className="flex items-center justify-between px-5 py-2 text-[10px] text-slate-500 font-medium">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span>📶</span><span>🔋</span>
            </span>
          </div>
          {/* Title bar */}
          <div className="px-5 pb-3 flex items-center justify-between">
            <span className="font-semibold text-[#1A1A1A]">Google Wallet</span>
            <span className="text-xs text-[#94A3B8]">···</span>
          </div>

          {/* Pass card — Google Wallet's loyalty card layout */}
          <div className="mx-3 rounded-2xl overflow-hidden shadow-md border border-slate-100" data-testid="google-pass-card">
            {/* Hero strip */}
            <div className="px-4 py-3" style={{ background: brand.primary_color, color: brand.text_color }}>
              <div className="flex items-center gap-2">
                {brand.logo_url ? (
                  <img src={brand.logo_url} alt="" className="w-8 h-8 rounded-md object-cover bg-white p-0.5" />
                ) : (
                  <div className="w-8 h-8 rounded-md flex items-center justify-center text-sm font-black bg-white" style={{ color: brand.primary_color }}>
                    {brand.name.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] opacity-80 uppercase tracking-wider">Carta fedeltà</div>
                  <div className="text-sm font-bold truncate">{brand.name}</div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="bg-white px-4 py-4">
              <div className="text-xs text-slate-500 mb-1">{customer.name}</div>
              <div className="flex items-end gap-2 mb-3">
                <span className="text-3xl font-black text-[#1A1A1A] tabular-nums leading-none">
                  {customer.points.toLocaleString('it-IT')}
                </span>
                <span className="text-sm text-slate-500 mb-1">punti</span>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold"
                  style={{ background: `${tierColor}20`, color: tierColor }}
                >
                  <Award size={10} />
                  {customer.tier}
                </span>
                <span className="text-[10px] text-slate-400">{customer.total_visits} visite</span>
              </div>

              {customer.recent_event && (
                <div className="mt-3 p-2 rounded-lg bg-slate-50 text-[11px] text-slate-600 border border-slate-100">
                  {customer.recent_event}
                </div>
              )}

              {/* QR */}
              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-center">
                <QRCodeSVG value={pass_metadata.barcode_message} size={130} bgColor="#FFFFFF" fgColor="#000000" level="M" includeMargin={false} />
              </div>
              <p className="text-center text-[10px] text-slate-400 mt-2 font-mono">{pass_metadata.barcode_message.slice(0, 14)}…</p>
            </div>

            {/* Footer links */}
            <div className="bg-slate-50 border-t border-slate-100">
              <button className="w-full px-4 py-3 flex items-center justify-between text-xs text-slate-700 font-medium hover:bg-slate-100">
                <span>Apri la tua bacheca</span>
                <ChevronRight size={14} className="text-slate-400" />
              </button>
            </div>
          </div>

          <p className="text-[10px] text-slate-500 mt-4 text-center px-6">
            Aperto da Wallet o richiamato automaticamente in cassa con NFC.
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, body }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100">
      <div className="w-10 h-10 rounded-xl bg-[#FDECE7] text-[#D44E20] flex items-center justify-center mb-3">
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <h4 className="text-sm font-semibold text-[#1A1A1A] mb-1">{title}</h4>
      <p className="text-xs text-[#64748B] leading-relaxed">{body}</p>
    </div>
  );
}
